import { Head, Link, router } from "@inertiajs/react"
import { type FormEvent, useEffect, useState } from "react"
import { Search } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { captureAnalyticsEvent } from "@/lib/analytics"
import type { PublicLocale } from "@/lib/public-links"

type Result = { id: number; name: string; summary: string; path: string; context: string }

const COPY = {
  en: {
    title: "Search",
    description: "Search verified places, ferries, trails, guides, and practical island information across the Xplore Croatia field guide.",
    placeholder: "Search places, ferries, trails...",
    prompt: "Enter at least two characters to search the published field guide.",
    short: "Keep typing — search starts at two characters.",
    empty: "No published results matched that search.",
  },
  de: {
    title: "Suche",
    description: "Durchsuche geprüfte Orte, Fähren, Wege, Reiseführer und praktische Inselinformationen im Xplore-Croatia-Feldführer.",
    placeholder: "Orte, Fähren und Wege suchen...",
    prompt: "Mindestens zwei Zeichen eingeben, um den veröffentlichten Reiseführer zu durchsuchen.",
    short: "Bitte weitertippen – die Suche beginnt ab zwei Zeichen.",
    empty: "Keine veröffentlichten Ergebnisse gefunden.",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export default function Show({
  locale,
  query,
  too_short,
  results,
}: {
  locale: PublicLocale
  query: string
  too_short: boolean
  results: Record<string, Result[]>
}) {
  const copy = COPY[locale]
  const [value, setValue] = useState(query)
  const groups = Object.entries(results)
  const resultCount = groups.reduce((total, [, records]) => total + records.length, 0)

  useEffect(() => {
    if (!query) return

    captureAnalyticsEvent("search_results_viewed", {
      query_length: query.trim().length,
      result_count: resultCount,
      too_short,
    })
  }, [ query, resultCount, too_short ])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    captureAnalyticsEvent("search_submitted", { query_length: value.trim().length })
    router.get(`/${locale}/search`, { q: value }, { preserveState: true })
  }

  return (
    <>
      <Head title={copy.title}>
        <meta name="description" content={copy.description} />
        <meta property="og:title" content={copy.title} />
        <meta property="og:description" content={copy.description} />
        <meta name="robots" content="noindex,follow" />
      </Head>
      <PublicShell locale={locale}>
        <main className="public-container py-8">
          <h1 className="public-page-title">{copy.title}</h1>
          <form onSubmit={submit} className="mt-5 flex max-w-2xl gap-2">
            <label className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-hairline bg-surface px-4">
              <Search className="h-4 w-4" />
              <input autoFocus value={value} onChange={(event) => setValue(event.target.value)} placeholder={copy.placeholder} className="w-full bg-transparent py-3 outline-none" />
            </label>
            <button className="btn btn-primary" type="submit">{copy.title}</button>
          </form>

          {!query ? (
            <p className="mt-6 text-ink-muted">{copy.prompt}</p>
          ) : too_short ? (
            <p className="mt-6 text-signal">{copy.short}</p>
          ) : groups.length === 0 ? (
            <p className="mt-6 text-ink-muted">{copy.empty}</p>
          ) : (
            <div className="mt-8 space-y-9">
              {groups.map(([type, records]) => (
                <section key={type}>
                  <h2 className="public-section-title">{type}</h2>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {records.map((result, position) => (
                      <Link
                        key={`${type}-${result.id}`}
                        href={result.path}
                        onClick={() => captureAnalyticsEvent("search_result_clicked", {
                          result_id: result.id,
                          result_type: type,
                          result_position: position + 1,
                        })}
                        className="public-card block p-5 no-underline"
                      >
                        <span className="public-kicker">{result.context}</span>
                        <strong className="public-card-title mt-1 block">{result.name}</strong>
                        <span className="public-card-description mt-2 block">{result.summary}</span>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </main>
      </PublicShell>
    </>
  )
}
